import React, { useEffect, useState } from 'react';
import AddressSchemaForm from './AddressSchemaForm';
import AddressSchemaFormManuallyUK from './UK/AddressSchemaFormManuallyUK';
import AddressAutofillFormUK from './UK/AddressAutofillFormUK';
import AddressAutocompleteSearchIE from './IE';
import { loadSchema, getCountries } from '../../../utils/address-schema';
import { required } from '../../../utils/formFieldValidators';
import { SelectField } from '../../01_atoms/FormElements';
import { useForm, useFormState } from 'react-final-form';
import { scrollToElement } from '../../../utils/scroll-to';

import styles from './index.module.scss';
import formattedTextStyles from '../../01_atoms/FormattedText/index.module.scss';

const Address = () => {
  const [schema, setSchema] = useState({});
  const formState = useFormState();
  const values = formState.values;
  const selectedCountry = values.address.country.value;
  const form = useForm();
  const [isManualAddress, setManualAddress] = useState(false);
  const [isAutoSwitchedToManualAddress, setAutoSwitchedToManualAddress] = useState(false);

  useEffect(() => {
    // Declare the data fetching function.
    const fetchData = async () => {
      return await loadSchema(selectedCountry);
    };
    fetchData().then((data) => {
      setSchema(data);
    });
  }, [selectedCountry]);

  useEffect(() => {
    // For the UK we use Address lookup with 2 select fields (Postcode, Address line 1),
    // therefore when users select an address, the value for the field will be saved as an object,
    // because it is select field. For the manual address form those fields are text fields, therefore
    // we need to convert value from object to text.
    if (isManualAddress) {
      if (typeof values.address.addressLine1 === 'object') {
        setAddressValues({
          addressLine1: values.address.addressLine1.label,
        });
      }
      if (typeof values.address.postalCode === 'object') {
        setAddressValues({
          postalCode: values.address.postalCode.label,
        });
      }
    }
  }, [isManualAddress]);

  useEffect(() => {
    if (selectedCountry === 'IE' && formState.submitFailed && formState.hasValidationErrors) {
      setManualAddress(true);
      setAutoSwitchedToManualAddress(true);
    }
  }, [selectedCountry, formState.submitFailed, formState.hasValidationErrors]);

  useEffect(() => {
    resetAddressFields();
    setAutoSwitchedToManualAddress(false);
  }, [schema]);

  /**
   * Updates the form state and therefore the appropriate inputs, with a
   * programmatically-set address, which the autocomplete & lookup utilities of
   * AddressAutofillFormUK facilitate.
   */
  const setAddressValues = (address = {}) => {
    // We need to set them individually, otherwise we overwrite other address selections
    Object.keys(address).forEach((key) => form.mutators.setValue(`address.${key}`, address[key]));
  };

  const resetAddressFields = () => {
    setAddressValues({
      addressLine1: '',
      addressLine2: '',
      locality: '',
      postalCode: '',
      administrativeArea: {},
    });
  };

  let AddressForm = <AddressSchemaForm country={selectedCountry} schema={schema} name="address" />;
  if (selectedCountry === 'IE') {
    AddressForm = (
      <>
        {(isAutoSwitchedToManualAddress || !isManualAddress) && (
          <div className={isManualAddress ? styles['address__ie-autocomplete'] : null}>
            <AddressAutocompleteSearchIE
              isRequired={true}
              hideSearchField={isManualAddress}
              onAddressSearch={(resp) => {
                if (resp.body.totalOptions === 0) {
                  setManualAddress(true);
                  setAutoSwitchedToManualAddress(true);

                  // Preconfigure Address fields if reformattedAddress values exist.
                  if (resp.body?.reformattedAddress?.length) {
                    setAddressValues({
                      addressLine1: resp.body.reformattedAddress[0],
                      addressLine2: resp.body.reformattedAddress[1],
                      locality: resp.body.reformattedAddress[2],
                      postalCode: resp.body.postcode,
                      administrativeArea: {
                        IE: resp.body.reformattedAddress[3],
                      },
                    });
                  }
                }
              }}
            />
          </div>
        )}

        {schema && isManualAddress && (
          <AddressSchemaForm country={selectedCountry} schema={schema} name="address" />
        )}

        {!isManualAddress && (
          <div
            className={`${styles['address__switch-mode-button']} ${formattedTextStyles['formatted-text']}`}
          >
            or{' '}
            <a
              href="#"
              onClick={(e) => {
                e.preventDefault();
                setManualAddress(true);
                setTimeout(() => {
                  const addressFieldIE = document.getElementById('address.addressLine1');
                  if (addressFieldIE) {
                    addressFieldIE.focus();
                  }
                }, 10);
              }}
              data-component="ie-manually-enter-address"
            >
              enter your address manually
            </a>
          </div>
        )}
        {isManualAddress && isAutoSwitchedToManualAddress && (
          <div
            className={`${styles['address__switch-mode-button']} ${formattedTextStyles['formatted-text']}`}
          >
            Wrong address?{' '}
            <a
              href="#"
              onClick={(e) => {
                e.preventDefault();
                setManualAddress(false);
                setAutoSwitchedToManualAddress(false);
                resetAddressFields();
                setTimeout(() => {
                  scrollToElement({ elementId: 'autocomplete-search-ie' });
                  const searchFieldIE = document.querySelector(
                    '#address-autocomplete-search__search input',
                  );
                  if (searchFieldIE) {
                    searchFieldIE.focus();
                  }
                }, 10);
              }}
            >
              Search again
            </a>
          </div>
        )}
        {isManualAddress && !isAutoSwitchedToManualAddress && (
          <div
            className={`${styles['address__switch-mode-button']} ${formattedTextStyles['formatted-text']}`}
          >
            or{' '}
            <a
              href="#"
              onClick={(e) => {
                e.preventDefault();
                setManualAddress(false);
                resetAddressFields();
                setTimeout(() => {
                  scrollToElement({ elementId: 'autocomplete-search-ie' });
                  const searchFieldIE = document.querySelector(
                    '#address-autocomplete-search__search input',
                  );
                  if (searchFieldIE) {
                    searchFieldIE.focus();
                  }
                }, 10);
              }}
            >
              search by your address or Eircode
            </a>
          </div>
        )}
      </>
    );
  }

  if (selectedCountry === 'GB') {
    AddressForm =
      isManualAddress || (values.address.addressLine1 && values.address.postalCode) ? (
        <AddressSchemaFormManuallyUK
          changeSchemaType={() => {
            setManualAddress(false);
            setAutoSwitchedToManualAddress(false);
            resetAddressFields();
            setTimeout(() => {
              scrollToElement({ elementId: 'address-autofill-uk' });
            }, 10);
          }}
          autoSwitched={isAutoSwitchedToManualAddress}
        />
      ) : (
        <AddressAutofillFormUK
          postalCode={values.address.postalCode}
          addressLine1={values.address.addressLine1}
          changeSchemaType={(schema, isAutoSwitched) => {
            setManualAddress(true);
            setAutoSwitchedToManualAddress(isAutoSwitched);
          }}
          setAddressValues={setAddressValues}
        />
      );
  }

  return (
    <div className={`${styles['address']} ${styles[`address__${selectedCountry}`]}`}>
      <SelectField
        name="address.country"
        label="Country"
        options={getCountries()}
        validate={required('Please select a country')}
        isRequired
        autoFill={false}
      />
      {AddressForm}
    </div>
  );
};

export default Address;
